/** @file Two-bone analytic IK (law of cosines) with softness, bend direction and stretch, mixed toward FK. */
import { affine } from '@pose2d/math/affine';
import { angle } from '@pose2d/math/angle';
import { trig } from '@pose2d/math/trig';
import { POSE_LAYOUT, type Pose } from '../contracts/pose';
import { effectiveParent } from '../pose/effectiveParent';
import { updateBoneWorld } from '../pose/updateBoneWorld';
import type { IkData, InheritMode, SkeletonData } from '../skeletonData';
import { aimLocalRotation } from './aimLocalRotation';
import { readWorld } from './readWorld';
import { solverChain } from './solverChain';
import { twoBoneAngles } from './twoBoneAngles';

const L = POSE_LAYOUT;
/** Offsets of the origin inside a world affine [a, b, c, d, tx, ty] (math/affine). */
const TX = 4;
const TY = 5;
/** Lengths, distances and determinants below this are degenerate (px / px²). */
const EPS = 1e-9;
const G = new Float64Array(L.WORLD_STRIDE);
const E = new Float64Array(L.WORLD_STRIDE);
const EI = new Float64Array(L.WORLD_STRIDE);
const M = new Float64Array(L.WORLD_STRIDE);
const ANGLES = new Float64Array(3);
const V: [number, number] = [0, 0];

/** Determinant of the linear part of m. */
function det2(m: Float64Array): number {
  return (m[0] ?? 0) * (m[3] ?? 0) - (m[1] ?? 0) * (m[2] ?? 0);
}

/** Channel `ch` of the applied block of `bone`. */
function ap(pose: Pose, bone: number, ch: number): number {
  return pose.applied[bone * L.LOCAL_STRIDE + ch] ?? 0;
}

/**
 * Solves constraint c in the parent's effective-parent space E (spec §6.5: the grandparent G for inherit normal;
 * bend is NOT multiplied by sign(det E), so a mirrored rig bends as its mirror image) and writes both applied
 * rotations (and the parent scaleX when stretching; the tip lands exactly only for a similarity parent frame with
 * child local y = 0). (ax, ay) is the parent joint in world. False keeps FK (target on the joint or not finite,
 * singular E or singular parent local frame).
 */
function solve(pose: Pose, data: SkeletonData, c: IkData, ik: number, ax: number, ay: number): boolean {
  const p = c.bones[0] ?? 0;
  const ch = c.bones[1] ?? 0;
  const k = ik * L.IK_STRIDE;
  readWorld(pose, data.bones[p]?.parent ?? -1, G);
  effectiveParent(E, G, (pose.inherit[p] ?? 0) as InheritMode);
  const detE = det2(E);
  if (!(Math.abs(detE) > EPS)) return false;
  // Target relative to the parent joint, in solve space: t = E⁻¹ (T − A).
  const tw = c.target * L.WORLD_STRIDE;
  affine.applyVec(affine.invert(EI, E), (pose.world[tw + TX] ?? 0) - ax, (pose.world[tw + TY] ?? 0) - ay, V);
  const tx = V[0];
  const ty = V[1];
  const d = trig.hypot(tx, ty);
  // The parent's local linear part at rotation 0: rotating the parent by r turns e and the child bone by r.
  affine.fromLocal(M, 0, 0, 0, ap(pose, p, L.SCALE_X), ap(pose, p, L.SCALE_Y), ap(pose, p, L.SHEAR_X), ap(pose, p, L.SHEAR_Y));
  // A target on the joint, or a non-finite one (NaN or infinite world), has no direction or reach: FK is kept.
  if (!(d >= EPS && d < Number.POSITIVE_INFINITY) || !(Math.abs(det2(M)) > EPS)) return false;
  affine.applyVec(M, ap(pose, ch, L.X), ap(pose, ch, L.Y), V);
  const ex = V[0];
  const ey = V[1];
  const childLen = (data.bones[ch]?.length ?? 0) * ap(pose, ch, L.SCALE_X);
  const stretch = (pose.ik[k + L.IK_STRETCH] ?? 0) >= 0.5;
  // Softness is authored in px; the solve space is E-scaled, so divide by E's mean scale. Stretch disables it.
  const softness = stretch ? 0 : (pose.ik[k + L.IK_SOFTNESS] ?? 0) / Math.sqrt(Math.abs(detE));
  const bend = (pose.ik[k + L.IK_BEND] ?? 0) >= 0.5 ? 1 : -1;
  const targetAngle = angle.radToDeg(trig.atan2(ty, tx));
  const eAngle = angle.radToDeg(trig.atan2(ey, ex));
  const pr = ap(pose, p, L.ROTATION);
  const cr = ap(pose, ch, L.ROTATION);
  let parentRot = pr;
  let reach = 0;
  let dd = d;
  if (trig.hypot(ex, ey) >= EPS && Math.abs(childLen) >= EPS) {
    twoBoneAngles(ANGLES, M, ex, ey, childLen, d, softness, bend);
    parentRot = targetAngle - (ANGLES[0] ?? 0) - eAngle;
    dd = ANGLES[1] ?? d;
    reach = ANGLES[2] ?? 0;
  } else if (trig.hypot(ex, ey) >= EPS) {
    parentRot = targetAngle - eAngle; // zero-length child: the elbow is the tip, aim it at the target
  }
  // Child: aim from the elbow at the reached point, under the solved parent (child inherit normal, §6.5).
  affine.fromLocal(M, 0, 0, parentRot, ap(pose, p, L.SCALE_X), ap(pose, p, L.SCALE_Y), ap(pose, p, L.SHEAR_X), ap(pose, p, L.SHEAR_Y));
  affine.applyVec(M, ap(pose, ch, L.X), ap(pose, ch, L.Y), V);
  const s = dd / d;
  const flip = ap(pose, ch, L.SCALE_X) < 0 ? 180 : 0;
  const childRot = Math.abs(childLen) < EPS ? cr : aimLocalRotation(M, V[0], V[1], tx * s, ty * s, ap(pose, ch, L.SHEAR_X)) + flip;
  const mix = pose.ik[k + L.IK_MIX] ?? 0;
  const a = pose.applied;
  a[p * L.LOCAL_STRIDE + L.ROTATION] = pr + angle.wrap180(parentRot - pr) * mix;
  a[ch * L.LOCAL_STRIDE + L.ROTATION] = cr + angle.wrap180(childRot - cr) * mix;
  // Stretch (R4): exact for a similarity parent frame with child local y = 0; approximate under a non-uniform parent.
  if (stretch && reach > EPS && d > reach) a[p * L.LOCAL_STRIDE + L.SCALE_X] = ap(pose, p, L.SCALE_X) * (1 + (d / reach - 1) * mix);
  return true;
}

/**
 * Writes parent+child applied rotations for constraint ik and recomputes their world transforms. Reads the FK
 * input from pose.applied (updatePose resets applied from local each frame) and the target's and grandparent's
 * worlds from pose.world (the update order computes them first). Always writes both worlds, even with mix 0.
 * A constraint that is not a two-bone chain throws IK_INVALID (solverChain).
 */
export function solveTwoBone(pose: Pose, data: SkeletonData, ik: number): void {
  const c = solverChain(data, ik, 2);
  const parent = c.bones[0] ?? 0;
  const child = c.bones[1] ?? 0;
  updateBoneWorld(pose, data, parent);
  const w = parent * L.WORLD_STRIDE;
  const mix = pose.ik[ik * L.IK_STRIDE + L.IK_MIX] ?? 0;
  if (mix !== 0 && solve(pose, data, c, ik, pose.world[w + TX] ?? 0, pose.world[w + TY] ?? 0)) updateBoneWorld(pose, data, parent);
  updateBoneWorld(pose, data, child);
}
